// Defensive error boundary that wraps a single PM view (Grid / Roadmap /
// MyWork / Templates). When a render-time crash happens inside, we show a
// small inline fallback instead of letting the page go blank — that was
// the actual symptom of the "click Roadmap → everything disappears" bug:
// a downstream render error would unmount the whole subtree.
//
// Resetting the boundary is automatic: whenever the `viewKey` prop changes
// (e.g. the user navigates to a different tab), we clear the error so the
// new view gets a fresh shot.

import { Component, ReactNode } from 'react';

interface Props {
  /** Identifier for the view currently mounted. Changing this resets the
   *  error state so navigating away gives the next view a clean slate. */
  viewKey: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

class ViewBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    // Surface in dev so the crash is visible while debugging the
    // intermittent roadmap bug; in prod this just goes to console.
    // eslint-disable-next-line no-console
    console.error('ViewBoundary caught:', error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (prev.viewKey !== this.props.viewKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="pm-view-boundary">
          <h3>Something went wrong rendering this view</h3>
          <p>
            We caught the error and kept the rest of the page running. Try
            switching tabs and back, or reload if it persists.
          </p>
          <details className="pm-view-boundary-details">
            <summary>Technical details</summary>
            <pre>{this.state.error.message}</pre>
          </details>
          <button
            type="button"
            className="pm-button"
            onClick={() => this.setState({ error: null })}
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ViewBoundary;
